import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { DailyAlert, Incident } from '../data/demoData';
import { fmtTs } from '../lib/globalTz';

function effectiveStatus(i: Incident) {
  const s = (i.status || '').toLowerCase();
  if (s === 'active') {
    const owner = (i.assignedTo || '').trim();
    if (!owner || owner === 'unassigned') return 'new';
  }
  if (s === 'active') return 'active';
  if (s === 'closed') return 'closed';
  if (s === 'new') return 'new';
  return s;
}

interface ChartsSectionProps {
  dailyAlerts: DailyAlert[];
  incidents: Incident[];
  darkMode: boolean;
}

const SEVERITY_COLORS = ['#d32f2f', '#ff9800', '#fbc02d', '#388e3c'];
const STATUS_COLORS = ['#d32f2f', '#0078d4', '#388e3c'];

export function ChartsSection({ dailyAlerts, incidents, darkMode }: ChartsSectionProps) {
  const severityCounts = [
    { name: 'High', value: incidents.filter(i => i.severity === 'High').length },
    { name: 'Medium', value: incidents.filter(i => i.severity === 'Medium').length },
    { name: 'Low', value: incidents.filter(i => i.severity === 'Low').length },
    { name: 'Informational', value: incidents.filter(i => i.severity === 'Informational').length },
  ].filter(d => d.value > 0);

  const statusCounts = [
    { name: 'New', value: incidents.filter(i => effectiveStatus(i) === 'new').length },
    { name: 'Active', value: incidents.filter(i => effectiveStatus(i) === 'active').length },
    { name: 'Closed', value: incidents.filter(i => effectiveStatus(i) === 'closed').length },
  ].filter(d => d.value > 0);

  const gridColor = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textColor = darkMode ? '#94a3b8' : '#555';

  const alertData = dailyAlerts.slice(-30).map(d => ({
    date: fmtTs(d.date, { month: 'short', day: 'numeric' }),
    count: d.count,
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="bg-card rounded-xl p-5 shadow">
        <h3 className="font-semibold text-foreground mb-4 text-sm">Alert Volume Trend <span className="font-normal text-muted-foreground text-xs">(last 30 days)</span></h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={alertData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: textColor }} tickLine={false} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: textColor }} tickLine={false} />
            <Tooltip contentStyle={{ background: darkMode ? '#1e293b' : '#fff', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
            <Line type="monotone" dataKey="count" stroke="#0078d4" strokeWidth={2.5} dot={false} name="Alerts" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-card rounded-xl p-5 shadow">
        <h3 className="font-semibold text-foreground mb-4 text-sm">Incidents by Severity</h3>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={severityCounts.length ? severityCounts : [{ name: 'No Data', value: 1 }]}
              cx="50%" cy="43%" innerRadius={50} outerRadius={75}
              dataKey="value" nameKey="name">
              {(severityCounts.length ? severityCounts : [{}]).map((_, i) => (
                <Cell key={i} fill={severityCounts.length ? SEVERITY_COLORS[i] : '#e0e0e0'} />
              ))}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: darkMode ? '#1e293b' : '#fff', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-card rounded-xl p-5 shadow">
        <h3 className="font-semibold text-foreground mb-4 text-sm">Incidents by Status</h3>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={statusCounts.length ? statusCounts : [{ name: 'No Data', value: 1 }]}
              cx="50%" cy="43%" outerRadius={75}
              dataKey="value" nameKey="name">
              {(statusCounts.length ? statusCounts : [{}]).map((_, i) => (
                <Cell key={i} fill={statusCounts.length ? STATUS_COLORS[i] : '#e0e0e0'} />
              ))}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: darkMode ? '#1e293b' : '#fff', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
